import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr'
import { Router } from '@angular/router';
import { IHttpConnectionOptions } from '@microsoft/signalr';
import { JwtUtils } from '../../auth/utilities/jwtUtils';
import { environment } from '../../../environments/environment';
import { ConsoleLogger } from '../../core/utilities/console-logger';
import { ToastrService } from 'ngx-toastr';
import { UserConnection } from './models/models';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { LandingPage } from '../../core/models/landing-page.enum';
import { HubReconnectScheduler } from '../../core/utilities/hub-reconnect-scheduler';



@Injectable()
export class UserPageTrackingService implements OnDestroy {
  private static instance: UserPageTrackingService;
  private changeFlagSource = new BehaviorSubject<boolean>(false);
  changeFlag$ = this.changeFlagSource.asObservable();
  private connection: signalR.HubConnection;
  public currentUserName = "";
  public allUsers: UserConnection[] = [];
  public usersOnCurrentPage: UserConnection[] = [];
  public testBeenConnected = false
  private isConnected = false;
  private isInitializing = false;
  private isShuttingDown = false;
  private reconnectScheduler = new HubReconnectScheduler('UserPageHub', () => {
    if (!this.isTrackingURL()) {
      return;
    }

    this.initializeConnection(this.currentUserName);
  });

  constructor(
    private router: Router,
    private toastrService: ToastrService,
  ) {
    if (UserPageTrackingService.instance) {
      ConsoleLogger.logInfo('Reusing existing UserPageHub instance');
      return UserPageTrackingService.instance;
    }
    
    UserPageTrackingService.instance = this;
  }
  
  ngOnDestroy() {
    this.disconnect();
  }

  private initializeConnection(userName: string) {
    if (this.isConnected || this.isInitializing) {
      ConsoleLogger.logInfo('UserPageHub connection already established or initializing, skipping initialization');
      return;
    }
    
    this.isInitializing = true;
    this.isShuttingDown = false;
    
    const connectionOptions: IHttpConnectionOptions = {
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
      accessTokenFactory: () => JwtUtils.accessToken
    };

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.userPageTrackingUrl, connectionOptions)
      .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 30000])
      .build();

    // 2x the server's 30s KeepAliveInterval
    this.connection.serverTimeoutInMilliseconds = 60000;

    this.registerEventHandlers();

    this.connection
    .start()
    .then(() => {
        ConsoleLogger.logInfo('UserPageHub connection established.');
        this.isInitializing = false;

        if (this.isShuttingDown) {
          this.connection.stop()
            .catch(err => ConsoleLogger.logInfo('Error stopping UserPageHub after disconnect:', err));
          return;
        }

        this.isConnected = true;
        this.reconnectScheduler.reset();
        this.joinPage(userName);
    })
    .catch(err => {
        console.warn('Error while starting UserPageHub connection:', err);
        this.isConnected = false;
        this.isInitializing = false;

        if (!this.isShuttingDown) {
          this.reconnectScheduler.schedule();
        }
    });
  }

  public logUrlChange(userName: string){
    this.currentUserName = userName;
    if(this.isTrackingURL() && (!this.connection || this.connection.state === signalR.HubConnectionState.Disconnected)){
      ConsoleLogger.logInfo('Connecting to UserPageHub');
      this.initializeConnection(userName);
      return;
    }

    if(this.connection && this.connection.state === signalR.HubConnectionState.Connected){
      this.joinPage(userName);
    }
  }

  public testConnection(){
    const timestamp = new Date().toISOString()
    if(this.testBeenConnected){
      if(!this.connection || this.connection.state !== signalR.HubConnectionState.Connected){
        console.error(`>[${timestamp}] USER-PAGE-TRACKER: Connection lost`)
        this.testBeenConnected = false;
        return;
      }else{
        console.log(`>[${timestamp}] USER-PAGE-TRACKER: Connected`)
      }
    }else{
      if(!this.connection){
        console.warn(`>[${timestamp}] USER-PAGE-TRACKER: Connection not yet established`)
        this.testBeenConnected = false;
        return;
      }
      if(this.connection.state !== signalR.HubConnectionState.Connected){
        console.warn(`>[${timestamp}] USER-PAGE-TRACKER: Not connected. Current state:${this.connection.state}`)
        this.testBeenConnected = false;
        return;
      }
      console.log(`>[${timestamp}] USER-PAGE-TRACKER: Connection Made`)
      this.testBeenConnected = true;
      return;
    }
  }

  private registerEventHandlers() {
    this.connection.onclose(() => {
      ConsoleLogger.logInfo("Lost UserPageHub connection");
      this.isConnected = false;
      this.isInitializing = false;

      if (!this.isShuttingDown && this.isTrackingURL()) {
        this.reconnectScheduler.schedule();
      }
    });

    this.connection.onreconnecting(() => {
      ConsoleLogger.logInfo("Reconnecting to UserPageHub");
      this.isConnected = false;
    });

    this.connection.onreconnected(connectionId => {
      ConsoleLogger.logInfo("Reconnected to UserPageHub");
      this.isConnected = true;
      this.reconnectScheduler.reset();
      if(this.isTrackingURL()){
        this.joinPage(this.currentUserName);
      }
    });
    
    // Listen for updates on all users
    this.connection.off("ReceiveAllUsers");
    this.connection.on("ReceiveAllUsers", (usersInGroup: UserConnection[]) => {
      this.allUsers = usersInGroup;
      this.triggerChangeFlag();
      this.filterUsersByCurrentPage();
    });
  }

  private filterUsersByCurrentPage() {
    const currentUrl = this.router.url;
    this.usersOnCurrentPage = this.allUsers.filter(user => user.url === currentUrl && user.userName !== this.currentUserName);
    this.usersOnCurrentPage.forEach(x => {
      x.showFullName = false;
      x.backgroundColor = this.ensureDarkColor(x.initials)
    });
  }

  private isListed(){
    if(this.connection || this.connection.state === signalR.HubConnectionState.Connected){
      const currentUrl = this.router.url;
      const index = this.allUsers.findIndex(user => user.url === currentUrl && user.userName === this.currentUserName);
      if(index == -1){
        this.joinPage(this.currentUserName)
      }
    }
  }

  //Not used: User if one wants to put up toastr notifications about people entering/leaving pages
  private filterUsersByCurrentPageWithToastr() {
    const currentUrl = this.router.url;
    const responseList = this.allUsers.filter(user => user.url === currentUrl && user.userName !== this.currentUserName);

    const newLeaves = this.usersOnCurrentPage.filter(a => !responseList.some(b => b.userName === a.userName));
    const newArrivals = responseList.filter(b => !this.usersOnCurrentPage.some(a => a.userName === b.userName));
    if(newArrivals.length > 0){
      newArrivals.forEach(x => {
        this.toastrService.warning(`You are on the same page with ${x.userName}`)
      })
    }
    if(newLeaves.length > 0){
      newLeaves.forEach(x => {
        this.toastrService.info(`You are no longer on the same page as ${x.userName}`)
      })
    }
    this.usersOnCurrentPage = responseList;
    this.usersOnCurrentPage.forEach(x => {
      x.showFullName = false;
      x.backgroundColor = this.ensureDarkColor(x.initials)
    });
  }

  public ensureDarkColor(initials: string): string {
    const hash = [];
    for (let i = 0; i < initials.length; i++) {
        hash.push( (initials.toUpperCase().charCodeAt(i) - 64) * 8 );
    }
    if(hash.length<2){
      hash.push(0); hash.push(0);
    }
    hash.push(hash[0] >= hash[1] ? hash[0] - hash[1] : hash[1] - hash[0])
    const r = hash[2];
    const g = hash[0];
    const b = hash[1];
    return `rgb(${r}, ${g}, ${b})`;
  }

  joinPage(userName: string) {
    this.currentUserName = userName;
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      if(this.isNotLandingPageUrl(this.router.url)){
        this.connection.invoke('JoinPage', this.router.url, userName);
      }else{
        this.leavePage(userName);
      }
    }else {
      console.warn('Cannot join page: UserPageHub connection is not in the connected state.');
    }
  }

  leavePage(userName: string) {
    this.currentUserName = userName;
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      this.connection.invoke('LeavePage');
    } else {
      console.warn('Cannot leave page: UserPageHub connection is not in the connected state.');
    }
  }

  private reset(){
    this.allUsers =[];
    this.usersOnCurrentPage = [];
  }

  private triggerChangeFlag() {
    this.changeFlagSource.next(!this.changeFlagSource.value);
  }

  private isTrackingURL(){
    const currentUrl = this.router.url;
    return (currentUrl.startsWith("/fleet-manager") || currentUrl.startsWith("/portal") || currentUrl.startsWith("/developer"))
  }

  private isNotLandingPageUrl(url: string): boolean {
    return !(Object.values(LandingPage) as string[]).includes(url);
  }
  
  public disconnect() {
    this.isShuttingDown = true;
    this.reconnectScheduler.cancel();

    if (!this.connection) {
      return;
    }
    
    if (this.connection.state === signalR.HubConnectionState.Disconnected) {
      this.isConnected = false;
      this.isInitializing = false;
      return;
    }

    this.connection.stop()
      .then(() => {
        this.isConnected = false;
        this.isInitializing = false;
        ConsoleLogger.logInfo('UserPageHub connection stopped.');
      })
      .catch(err => {
        this.isConnected = false;
        this.isInitializing = false;
        ConsoleLogger.logInfo('Error stopping UserPageHub connection:', err);
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/signalR/userPageTrackingService.ts